import { Image, Platform } from 'react-native'

import { images } from '@zet/assets'
import { MY_TEAMS_ADVISOR } from '@zet/constants'
import { TierBasedCategoryCoins } from '@zet/constants/tierBasedCategoryCoins'
import { AgentDetails } from '@zet/customer'
import { SH3, colors } from '@zet/design-system'
import DoneFilled from '@zet/design-system/svg/DoneFilled'
import DoneOutlined from '@zet/design-system/svg/DoneOutlined'
import LockOutLined from '@zet/design-system/svg/lockOutlined'
import { APP_NAME } from '@zet/end-points'
import {
  AgentTiers,
  BEGINNER,
  EXPERT,
  GENIUS,
  PRO,
} from '@zet/feature-loyalty/utils/Constants'
import { i18n } from '@zet/localization'
import { HomeLoans, SbmCardIcon } from '@zet/svg'
import AtmCard from '@zet/svg/atmCard'
import Bank from '@zet/svg/bank'
import Cars24 from '@zet/svg/cars24'
import CoinBag from '@zet/svg/coinBag'
import CoinRupee from '@zet/svg/coinRupee'
import DematAccount from '@zet/svg/dematAccount'
import GoldBar from '@zet/svg/goldBar'
import Insurance from '@zet/svg/insurance'
import Investments from '@zet/svg/investments'
import LifeTimeFree from '@zet/svg/lifeTimeFree.svg'
import MoneyBag from '@zet/svg/moneyBag'
import MyBusiness from '@zet/svg/myBusiness'
import OnHold from '@zet/svg/onHold'
import OtherLoans from '@zet/svg/otherLoans'
import PiggyBank from '@zet/svg/piggyBank'
import RechargeBill from '@zet/svg/rechargeBill'
import Saving from '@zet/svg/saving'
import TwoWheelerLoan from '@zet/svg/twoWheelerLoan'
import { Categories } from '@zet/utils/Constants'
import { UseFormSetValue, UseFormTrigger } from 'react-hook-form'

import { IBrandComsData, ILoyaltyGifts } from '../api/brands/types'
import ExpertTitle from '../assets/svg/expertTitle.svg'
import GeniusTitle from '../assets/svg/geniusTitle.svg'
import ProTitle from '../assets/svg/proTitle.svg'
import Smartwatch from '../assets/svg/smartwatch.svg'
import WiredEarphones from '../assets/svg/wiredEarphones.svg'
import WirelessEarphones from '../assets/svg/wirelessEarphones.svg'
import {
  CBMLogoUrl,
  MAGNET,
  extraLoyaltyPayoutEligibleCategory,
} from './constants'

type getTotalPayoutTextProps = {
  tier: string
  categoryId?: string | number
  isPercentagePayout: boolean
  extraPayoutBenefit: number
  payoutText?: string
}

const getSvgIcon = (categoryId: number) => {
  switch (categoryId) {
    case Categories.CREDIT_CARDS:
      return <AtmCard height={55} width={55} />
    case Categories.PERSONAL_LOANS:
      return <MoneyBag height={55} width={55} />
    case Categories.BUSINESS_LOANS:
      return <MyBusiness height={55} width={55} />
    case 11:
      return <CoinBag />
    case Categories.PAY_LATER_EMI_CARDS:
      return <PiggyBank />
    case 12:
      return <CoinRupee />
    case 2:
      return <GoldBar />
    case Categories.BANK_ACCOUNTS:
      return <Bank height={55} width={55} />
    case 8:
      return <RechargeBill />
    case Categories.SECURED_CREDIT_CARDS:
      return <SbmCardIcon />
    case 21:
      return <Image source={images.zetIcon} className="h-[50] w-[50]" />
    case 22:
      return <TwoWheelerLoan />
    case Categories.CIBIL_BUILDER_LOANS:
      return (
        <Image
          source={{
            uri: CBMLogoUrl,
          }}
          resizeMode="contain"
          className="h-[45] w-[45]"
        />
      )
    case Categories.DEMAT_ACCOUNTS:
      return <DematAccount width={55} height={55} />
    case Categories.OTHER_LOANS:
      return <OtherLoans width={55} height={55} />
    case Categories.INSURANCE:
      return <Insurance width={55} height={55} />
    case Categories.HOME_LOANS:
      return <HomeLoans height={55} width={55} />
    case Categories.CARS24:
      return <Cars24 />
    case Categories.INVESTMENTS:
      return <Investments width={55} height={55} />
    default:
      return <Saving />
  }
}

const getTagType = (
  content?: IBrandComsData,
  onHold?: boolean,
  isHighApproval?: boolean,
  partnerId?: string | number,
) => {
  const getTagTypeForCard = () => {
    if (partnerId?.toString() === MAGNET) {
      return 'MAGNET'
    } else if (onHold) {
      return 'HOLD'
    } else if (isHighApproval) {
      return 'HIGH_APPROVAL'
    } else if (content?.isNewBrand) {
      return 'NEW_BRAND_LIVE'
    } else if (content?.isPayoutChanged) {
      return 'PAYOUT_CHANGED'
    } else {
      return ''
    }
  }

  switch (getTagTypeForCard()) {
    case 'NEW_BRAND_LIVE':
      return {
        iconName: 'Star',
        title: i18n.t('recommendationCard.brandLive'),
        bgColor: colors.decorative.bee[100],
      }
    case 'PAYOUT_CHANGED':
      return {
        iconName: 'TagInfo',
        title: i18n.t('recommendationCard.payoutChanged'),
        bgColor: '#3A922C',
        hideTag: APP_NAME === MY_TEAMS_ADVISOR,
      }
    case 'HOLD':
      return {
        iconName: 'svg',
        icon: <OnHold />,
        title: i18n.t('brandPage.onHold'),
        bgColor: '#7E89A1',
      }
    case 'HIGH_APPROVAL':
      return {
        iconName: 'Star',
        title: i18n.t('brandPage.highApproval'),
        bgColor: '#B78A34',
      }
    case 'MAGNET':
      return {
        iconName: '',
        icon: <LifeTimeFree />,
        title: i18n.t('brandPage.lifeTimeFree'),
        bgColor: '#B78A34',
        hideTag: APP_NAME === MY_TEAMS_ADVISOR,
      }
    default:
      return {
        iconName: '',
        title: '',
        bgColor: '',
      }
  }
}

const getApprovalChanceColor = (colorIdentity: string) => {
  switch (colorIdentity) {
    case 'HIGH':
      return 'text-success-100 mt-1'
    case 'MEDIUM':
      return 'text-warning-100 mt-1'
    default:
      return 'text-error-100 mt-1'
  }
}

const getRecommendationReason = (
  creteria: string,
  reason?: string,
  name?: string,
  partnerName?: string,
) => {
  switch (creteria) {
    case 'LEAD_EXISTS':
      return Platform.OS === 'web'
        ? i18n.t('recommendationBottomSheet.applicationStartedByDiffAdvisor')
        : i18n.t('recommendationBottomSheet.alreadyStartedWithBrand', {
            name: name ?? ' ',
            partnerName: partnerName ?? ' ',
          })
    case 'MIN_INCOME':
      return Platform.OS === 'web'
        ? i18n.t('recommendationBottomSheet.salaryCriteriaNotMeet')
        : i18n.t('recommendationBottomSheet.customerSpecificSalaryCriteria', {
            name,
            partnerName: partnerName,
          })
    case 'PINCODE':
      return Platform.OS === 'web'
        ? i18n.t('recommendationBottomSheet.brandNotAvlAtPinCode')
        : i18n.t('recommendationBottomSheet.approvalChancePinCode')
    default:
      return reason
  }
}

const getSubtitle = (earnUpto: number, id: number) => {
  if (id === 18) {
    return i18n.t('categories.lifetimeEarning')
  } else if (earnUpto) {
    return i18n.t('categories.earnUpto', { earnUpto })
  }
  return ''
}

const payoutText = (tier: any, tierData: any) => {
  switch (tier?.cohort) {
    case BEGINNER:
      return {
        tierText: i18n.locale === 'en' ? i18n.t('brandDetails.youAre') : '',
        tier: AgentTiers.BEGINNER,
        completeSalesText: i18n.t('brandDetails.completeSales', {
          sales: tierData?.BEGINNER?.targetSales,
          days: tierData?.BEGINNER?.targetDays,
          tier: AgentTiers.PRO,
          currentTier: AgentTiers.BEGINNER,
        }),
      }
    case PRO:
      return {
        tierText: i18n.locale === 'en' ? i18n.t('brandDetails.youAre') : '',
        tier: AgentTiers.PRO,
        completeSalesText: i18n.t('brandDetails.completeSales', {
          sales: tierData?.PRO?.targetSales,
          days: tierData?.PRO?.targetDays,
          tier: AgentTiers.EXPERT,
          currentTier: AgentTiers.PRO,
        }),
      }
    case EXPERT:
      return {
        tierText: i18n.locale === 'en' ? i18n.t('brandDetails.youAreAn') : '',
        tier: AgentTiers.EXPERT,
        completeSalesText: i18n.t('brandDetails.completeSales', {
          sales: tierData?.EXPERT?.targetSales,
          days: tierData?.EXPERT?.targetDays,
          tier: AgentTiers.GENIUS,
          currentTier: AgentTiers.EXPERT,
        }),
      }
    case GENIUS:
      return {
        tierText: i18n.locale === 'en' ? i18n.t('brandDetails.youAre') : '',
        tier: AgentTiers.GENIUS,
        completeSalesText:
          tierData?.GENIUS?.targetSales > 0 && tierData?.GENIUS?.targetDays > 0
            ? i18n.t('brandDetails.youAreGeniusPending', {
                sales: tierData?.GENIUS?.targetSales,
                days: tierData?.GENIUS?.targetDays,
              })
            : i18n.t('brandDetails.youAreGeniusComplete'),
      }
    default:
      return {
        tierText: '',
        tier: '',
        completeSalesText: '',
      }
  }
}

function handleIcon(status: string) {
  switch (status) {
    case 'ACHIEVED':
      return <DoneOutlined size={17} />
    case 'CURRENT':
      return <DoneFilled color={colors.success[100]} size={17} />
    case 'AVAILABLE':
      return <LockOutLined size={17} />
  }
}
function handlePayoutTableIconAndTitle(tier: any, tierData: any) {
  const payoutTableIconAndTitle = [
    {
      title: (
        <SH3
          className={`${
            tierData?.BEGINNER?.status === 'CURRENT'
              ? 'text-success-200'
              : 'text-grays-100'
          } ml-1`}
        >
          {i18n.t('loyaltyDetails.beginner')}
        </SH3>
      ),
      icon: handleIcon(tierData?.BEGINNER?.status),
      bgColor: tierData?.BEGINNER?.status === 'CURRENT' ? 'bg-success-10' : '',
      border:
        tierData?.BEGINNER?.status !== 'CURRENT'
          ? 'border-r border-grays-40'
          : '',
    },
    {
      title: (
        <SH3
          className={`${
            tierData?.PRO?.status === 'CURRENT'
              ? 'text-success-200'
              : 'text-grays-100'
          } ml-1`}
        >
          {i18n.t('loyaltyDetails.pro')}
        </SH3>
      ),
      icon: handleIcon(tierData?.PRO?.status),
      bgColor: tierData?.PRO?.status === 'CURRENT' ? 'bg-success-10' : '',
      border:
        tierData?.PRO?.status !== 'CURRENT' ? 'border-r border-grays-40' : '',
    },
    {
      title: (
        <SH3
          className={`${
            tierData?.EXPERT?.status === 'CURRENT'
              ? 'text-success-200'
              : 'text-grays-100'
          } ml-1`}
        >
          {i18n.t('loyaltyDetails.expert')}
        </SH3>
      ),
      icon: handleIcon(tierData?.EXPERT?.status),
      bgColor: tierData?.EXPERT?.status === 'CURRENT' ? 'bg-success-10' : '',
      border:
        tierData?.EXPERT?.status !== 'CURRENT'
          ? 'border-r border-grays-40'
          : '',
    },
    {
      title: (
        <SH3
          className={`${
            tierData?.GENIUS?.status === 'CURRENT'
              ? 'text-success-200'
              : 'text-grays-100'
          } ml-1`}
        >
          {i18n.t('loyaltyDetails.genius')}
        </SH3>
      ),
      icon:
        tier?.cohort === GENIUS ? (
          <DoneFilled color={colors.success[100]} size={17} />
        ) : (
          handleIcon(tierData?.GENIUS?.status)
        ),
      bgColor: tierData?.GENIUS?.status === 'CURRENT' ? 'bg-success-10' : '',
      border:
        tierData?.GENIUS?.status !== 'CURRENT'
          ? 'border-r border-grays-40'
          : '',
    },
  ]
  return payoutTableIconAndTitle
}

function handleBasePayout(
  tier: any,
  isPercentagePayout?: boolean,
  basePayoutValue?: string | number,
) {
  const basePayout = [
    {
      amount: (
        <SH3
          className={`${tier?.cohort === BEGINNER ? 'text-success-100' : ''}`}
        >
          {isPercentagePayout ? basePayoutValue + '%' : '₹' + basePayoutValue}
        </SH3>
      ),
      bgColor: tier?.cohort === BEGINNER ? 'bg-success-10' : '',
    },
    {
      amount: (
        <SH3 className={`${tier?.cohort === PRO ? 'text-success-100' : ''}`}>
          {isPercentagePayout ? basePayoutValue + '%' : '₹' + basePayoutValue}
        </SH3>
      ),
      bgColor: tier?.cohort === PRO ? 'bg-success-10' : '',
    },
    {
      amount: (
        <SH3 className={`${tier?.cohort === EXPERT ? 'text-success-100' : ''}`}>
          {isPercentagePayout ? basePayoutValue + '%' : '₹' + basePayoutValue}
        </SH3>
      ),
      bgColor: tier?.cohort === EXPERT ? 'bg-success-10' : '',
    },
    {
      amount: (
        <SH3 className={`${tier?.cohort === GENIUS ? 'text-success-100' : ''}`}>
          {isPercentagePayout ? basePayoutValue + '%' : '₹' + basePayoutValue}
        </SH3>
      ),
      bgColor: tier?.cohort === GENIUS ? 'bg-success-10' : '',
    },
  ]
  return basePayout
}

function handleExtraPayoutForTier(
  extraPayout: number,
  isPercentagePayout: boolean,
) {
  if (isPercentagePayout) {
    return extraPayout + '%'
  } else {
    return '₹' + extraPayout
  }
}

function handleExtraPayout(tier: any, loyaltyPayout: any) {
  const isPercentagePayout = loyaltyPayout?.partnerDiscountType !== 'flat'
  const extraPayout = [
    {
      amount: (
        <SH3
          style={{
            color:
              tier?.cohort === BEGINNER
                ? colors.success[10]
                : colors.neutral.white100,
          }}
        >
          {'00'}
        </SH3>
      ),
      bgColor: tier?.cohort === BEGINNER ? 'bg-success-10' : '',
    },
    {
      amount: (
        <SH3 className={`${tier?.cohort === PRO ? 'text-success-100' : ''}`}>
          {loyaltyPayout?.PRO
            ? handleExtraPayoutForTier(loyaltyPayout?.PRO, isPercentagePayout)
            : ''}
        </SH3>
      ),
      bgColor: tier?.cohort === PRO ? 'bg-success-10' : '',
    },
    {
      amount: (
        <SH3 className={`${tier?.cohort === EXPERT ? 'text-success-100' : ''}`}>
          {loyaltyPayout?.EXPERT
            ? handleExtraPayoutForTier(
                loyaltyPayout?.EXPERT,
                isPercentagePayout,
              )
            : ''}
        </SH3>
      ),
      bgColor: tier?.cohort === EXPERT ? 'bg-success-10' : '',
    },
    {
      amount: (
        <SH3 className={`${tier?.cohort === GENIUS ? 'text-success-100' : ''}`}>
          {loyaltyPayout?.GENIUS
            ? handleExtraPayoutForTier(
                loyaltyPayout?.GENIUS,
                isPercentagePayout,
              )
            : ''}
        </SH3>
      ),
      bgColor: tier?.cohort === GENIUS ? 'bg-success-10' : '',
    },
  ]
  return extraPayout
}

function calculateCoinsForTier(tierType: string, categoryId?: number | string) {
  const extraCoinsValue = TierBasedCategoryCoins?.[tierType]?.[categoryId]
  return extraCoinsValue ?? '0'
}

function handleExtraCoins(tier: any, categoryId?: number | string) {
  const extraCoins = [
    {
      coins: (
        <SH3
          className={`${
            tier?.cohort === BEGINNER ? 'text-success-100' : ''
          }  mr-1`}
        >
          {calculateCoinsForTier('BEGINNER', categoryId)}
        </SH3>
      ),
      bgColor: tier?.cohort === BEGINNER ? 'bg-success-10' : '',
      cohort: BEGINNER,
    },
    {
      coins: (
        <SH3
          className={`${tier?.cohort === PRO ? 'text-success-100' : ''} mr-1`}
        >
          {calculateCoinsForTier('PRO', categoryId)}
        </SH3>
      ),
      bgColor: tier?.cohort === PRO ? 'bg-success-10' : '',
      cohort: PRO,
    },
    {
      coins: (
        <SH3
          className={`${
            tier?.cohort === EXPERT ? 'text-success-100' : ''
          } mr-1`}
        >
          {calculateCoinsForTier('EXPERT', categoryId)}
        </SH3>
      ),
      bgColor: tier?.cohort === EXPERT ? 'bg-success-10' : '',
      cohort: EXPERT,
    },
    {
      coins: (
        <SH3
          className={`${
            tier?.cohort === GENIUS ? 'text-success-100' : ''
          } mr-1`}
        >
          {calculateCoinsForTier('GENIUS', categoryId)}
        </SH3>
      ),
      bgColor: tier?.cohort === GENIUS ? 'bg-success-10' : '',
      cohort: GENIUS,
    },
  ]
  return extraCoins
}

const giftItems = {
  WIRED_EARPHONES: {
    icon: <WiredEarphones />,
    text: 'brandDetails.wiredEarphones',
  },
  WIRELESS_EARPHONES: {
    icon: <WirelessEarphones />,
    text: 'brandDetails.premiumEarphones',
  },
  SMARTWATCH: {
    icon: <Smartwatch />,
    text: 'brandDetails.smartWatch',
  },
}
function handleTierBenefit(
  tierData: any,
  tier: any,
  loyaltyGifts: ILoyaltyGifts,
) {
  const tierBenefit = [
    {
      titleIcon: <ProTitle className="mb-3 mt-5 self-center" />,
      extraPayoutPercentage: 5,
      giftItem: {
        icon: loyaltyGifts?.PRO ? giftItems?.[loyaltyGifts?.PRO]?.icon : '',
        text: loyaltyGifts?.PRO ? giftItems?.[loyaltyGifts?.PRO]?.text : '',
      },
      coins: 750,
      titleText: i18n.t(`brandDetails.PRO.${tierData?.PRO?.status}`, {
        sales: tierData?.BEGINNER?.targetSales,
        days: tierData?.BEGINNER?.targetDays,
      }),
      showTag: tier?.cohort === PRO,
    },
    {
      titleIcon: <ExpertTitle className="mb-3 mt-5 self-center" />,
      extraPayoutPercentage: 10,
      giftItem: {
        icon: loyaltyGifts?.EXPERT
          ? giftItems?.[loyaltyGifts?.EXPERT]?.icon
          : '',
        text: loyaltyGifts?.EXPERT
          ? giftItems?.[loyaltyGifts?.EXPERT]?.text
          : '',
      },
      coins: 1000,
      titleText: i18n.t(`brandDetails.EXPERT.${tierData?.EXPERT?.status}`, {
        sales: tierData?.PRO?.targetSales,
        days: tierData?.PRO?.targetDays,
      }),
      showTag: tier?.cohort === EXPERT,
    },
    {
      titleIcon: <GeniusTitle className="mb-3 mt-5 self-center" />,
      extraPayoutPercentage: 15,
      giftItem: {
        icon: loyaltyGifts?.GENIUS
          ? giftItems?.[loyaltyGifts?.GENIUS]?.icon
          : '',
        text: loyaltyGifts?.GENIUS
          ? giftItems?.[loyaltyGifts?.GENIUS]?.text
          : '',
      },
      coins: 2000,
      titleText: i18n.t(`brandDetails.GENIUS.${tierData?.GENIUS?.status}`, {
        sales:
          tierData?.GENIUS?.status === 'CURRENT'
            ? tierData?.GENIUS?.targetSales
            : tierData?.EXPERT?.targetSales,
        days:
          tierData?.GENIUS?.status === 'CURRENT'
            ? tierData?.GENIUS?.targetDays
            : tierData?.EXPERT?.targetDays,
      }),
      showTag: tier?.cohort === GENIUS,
    },
  ]
  return tierBenefit
}

function handlePrefill(
  setValue: UseFormSetValue<{
    fullName: string
    phoneNumber: string
    pinCode: string
    email: string
    advisorCode?: string | undefined
  }>,
  trigger: UseFormTrigger<{
    phoneNumber: string
    fullName: string
    pinCode: string
    email: string
    advisorCode?: string | undefined
  }>,
  agentData?: AgentDetails,
) {
  if (sessionStorage.getItem('fullName')) {
    setValue('fullName', sessionStorage.getItem('fullName'))
    trigger('fullName')
  }
  if (sessionStorage.getItem('phoneNumber')) {
    setValue('phoneNumber', sessionStorage.getItem('phoneNumber'))
    trigger('phoneNumber')
  }
  if (sessionStorage.getItem('email')) {
    setValue('email', sessionStorage.getItem('email'))
    trigger('email')
  }
  if (sessionStorage.getItem('pinCode')) {
    setValue('pinCode', sessionStorage.getItem('pinCode'))
    trigger('pinCode')
  }
  if (sessionStorage.getItem('advisorCode')) {
    setValue('advisorCode', sessionStorage.getItem('advisorCode'))
    trigger('advisorCode')
  } else if (agentData?.advisorCode) {
    setValue('advisorCode', agentData?.advisorCode)
    trigger('advisorCode')
  }
}

function handleSettingValueToSessionStorage(getValues: any, agentData: any) {
  sessionStorage.setItem('fullName', getValues('fullName'))
  sessionStorage.setItem('phoneNumber', getValues('phoneNumber'))
  sessionStorage.setItem('pinCode', getValues('pinCode'))
  sessionStorage.setItem('email', getValues('email'))
  if (agentData?.isDsa && Boolean(getValues('advisorCode'))) {
    sessionStorage.setItem('advisorCode', getValues('advisorCode'))
  }
}
const getTotalPayoutText = ({
  tier,
  categoryId,
  isPercentagePayout,
  extraPayoutBenefit,
  payoutText,
}: getTotalPayoutTextProps) => {
  const extractedPayoutValue = parseFloat(payoutText?.replace(/[^0-9.]/g, ''))

  // Check if it's Bank Account (5) or Demat Account (6)
  const isBankOrDematAccount = categoryId === 5 || categoryId === 6

  if (
    tier === 'BEGINNER' ||
    !extraLoyaltyPayoutEligibleCategory.includes(categoryId?.toString())
  ) {
    return payoutText
  } else if (isPercentagePayout) {
    if (extraPayoutBenefit) {
      return extractedPayoutValue + '%' + ' + ' + extraPayoutBenefit + '%'
    }
    return extractedPayoutValue + '%'
  } else {
    // For Bank/Demat accounts, the payoutText already has "Flat ₹" prefix
    if (isBankOrDematAccount) {
      if (extraPayoutBenefit) {
        return payoutText + ' + ' + '₹' + extraPayoutBenefit
      }
      return payoutText
    }

    if (extraPayoutBenefit) {
      return '₹' + extractedPayoutValue + ' + ' + '₹' + extraPayoutBenefit
    }
    return '₹' + extractedPayoutValue
  }
}

const getPayoutText = (type: 'flat' | 'percentage', value: number) => {
  if (type === 'flat') {
    return `₹${value}`
  } else {
    return `${value}%`
  }
}

const getApprovalRateState = ({
  approvalRate,
  approvalRateText,
}: {
  approvalRate?: 'HIGH' | 'MEDIUM' | 'LOW'
  approvalRateText?: string
}) => {
  switch (approvalRate) {
    case 'HIGH':
      return {
        icon: 'DoneOutlined',
        textStyle: 'text-success-100',
        iconColor: colors.success[100],
        text: i18n.t('recommendationCard.approvalChance', {
          initialText: approvalRateText || 'High',
        }),
      }
    case 'MEDIUM':
      return {
        icon: 'DoneOutlined',
        textStyle: 'text-warning-150',
        iconColor: colors.warning[150],
        text: i18n.t('recommendationCard.approvalChance', {
          initialText: approvalRateText || 'Medium',
        }),
      }
    case 'LOW':
      return {
        icon: 'ErrorOutlined',
        textStyle: 'text-error-150',
        iconColor: colors.error[150],
        text: i18n.t('recommendationCard.approvalChance', {
          initialText: approvalRateText || 'Low',
        }),
      }
    default:
      return {
        icon: 'ErrorOutlined',
        textStyle: 'text-error-150',
        iconColor: colors.error[150],
        text: i18n.t('recommendationCard.notEligible'),
      }
  }
}

const parseCMSScreenOptions = (options: any) => {
  return options?.JSONOptions
    ? JSON.parse(options?.JSONOptions)
    : { ...options }
}

function normalizeMultilineText(
  text: string | undefined,
  variables: Record<string, string> = {},
  regEx?: RegExp,
) {
  if (!text) return ''

  let parsed = text

  Object.keys(variables).forEach((key) => {
    parsed = parsed?.replaceAll(`{{${key}}}`, variables[key] ?? '')
  })

  if (regEx) {
    parsed = parsed?.replace(regEx, '')
  }

  parsed = parsed?.replace(/\\n/g, '\n')

  return parsed
}

export {
  calculateCoinsForTier,
  getApprovalChanceColor,
  getApprovalRateState,
  getPayoutText,
  getRecommendationReason,
  getSubtitle,
  getSvgIcon,
  getTagType,
  getTotalPayoutText,
  handleBasePayout,
  handleExtraCoins,
  handleExtraPayout,
  handlePayoutTableIconAndTitle,
  handlePrefill,
  handleSettingValueToSessionStorage,
  handleTierBenefit,
  normalizeMultilineText,
  parseCMSScreenOptions,
  payoutText,
}
